import axios from 'axios';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import { ENV_CONFIG } from '../../shared/config/env.config';
import { toast } from 'react-toastify';
import { assignLetters } from '../../shared/services/userSessionService';
import { globalLoader } from '../../shared/UI/Uicontext';

type DynamicBaseQueryArgs = FetchArgs & {
  service?: string;
  /**
   * Opt out of the app's global auth headers (Authorization / Token).
   *
   * Required for endpoints that are not part of the EMIS estate — e.g. the
   * raw AWS API Gateway host behind `nsNopEmisUrl`, which authenticates with
   * `x-api-key` only. API Gateway rejects requests that carry an unexpected
   * Authorization header, and the extra headers also have to be whitelisted
   * in the gateway's CORS config or the preflight fails.
   */
  skipAuth?: boolean;
};

const SERVICE_URL_MAP: Record<string, string> = {
  php1: ENV_CONFIG.php1Url,
  php3: ENV_CONFIG.php3Url,
  mobilephp: ENV_CONFIG.mobileUrlphp,
  djangoTnEmis: ENV_CONFIG.djangoTnEmisUrl,
  djangoInstitute: ENV_CONFIG.djangoInstituteUrl,
  djangoEmis: ENV_CONFIG.djangoEmisUrl,
  // g2c PHP (CodeIgniter) — coaching-centre eSign endpoint ({txn, signXML}).
  g2c: ENV_CONFIG.g2cUrl,
  emisLogin: ENV_CONFIG.emisLoginUrl,
  emisAuth: ENV_CONFIG.emisAuthUrl,
  djangoSupport: ENV_CONFIG.DjangoSupportUrl,
  emis_login: ENV_CONFIG.emis_login,
  nsNopUrl: ENV_CONFIG.nsNopUrl,
  nsNopEmisUrl: ENV_CONFIG.nsNopEmisUrl,
  emisExt: ENV_CONFIG.emisExtUrl,
  emisSample: ENV_CONFIG.emisSampleUrl,
};

const getApiErrorMessage = (status: number | string, data: any) => {
  const backendMessage =
    data?.message ||
    data?.error ||
    data?.detail ||
    data?.msg ||
    (typeof data === 'string' ? data : '');

  switch (status) {
    case 'FETCH_ERROR':
      return (
        backendMessage ||
        'Unable to connect to server. Please check your network or server status.'
      );

    case 0:
      return 'Unable to connect to server. Please check your internet connection or server status.';

    case 400:
      return (
        backendMessage || 'Invalid request. Please check the submitted data.'
      );

    case 401:
      return backendMessage || 'Your session has expired. Please login again.';

    case 403:
      return (
        backendMessage || 'You do not have permission to perform this action.'
      );

    case 404:
      return backendMessage || 'Requested data was not found.';

    case 408:
      return backendMessage || 'Request timeout. Please try again.';

    case 422:
      return (
        backendMessage || 'Validation failed. Please check the entered details.'
      );

    case 429:
      return (
        backendMessage || 'Too many requests. Please try again after some time.'
      );

    case 500:
      return 'Internal server error occurred.';

    case 502:
      return (
        backendMessage || 'Bad gateway. Server is not responding properly.'
      );

    case 503:
      return (
        backendMessage ||
        'Service temporarily unavailable. Please try again later.'
      );

    case 504:
      return (
        backendMessage || 'Gateway timeout. Server took too long to respond.'
      );

    default:
      return backendMessage || 'Something went wrong. Please try again later.';
  }
};

export const dynamicBaseQuery: BaseQueryFn<
  string | DynamicBaseQueryArgs,
  unknown,
  FetchBaseQueryError
> = async (args, _api, _extraOptions) => {
  let service: string = 'php1';
  let modifiedArgs: FetchArgs;

  globalLoader.show();

  let skipAuth = false;

  if (typeof args === 'string') {
    modifiedArgs = { url: args };
  } else {
    const {
      service: extractedService,
      skipAuth: extractedSkipAuth,
      ...restArgs
    } = args;
    service = extractedService ?? 'php1';
    skipAuth = extractedSkipAuth ?? false;
    modifiedArgs = restArgs;
  }

  const baseUrl = SERVICE_URL_MAP[service];

  const {
    url,
    method = 'GET',
    body,
    params,
    headers: argHeaders,
  } = modifiedArgs;

  let authKey: any = skipAuth ? null : localStorage.getItem('AuthorizationKey');
  const rawToken = skipAuth ? null : localStorage.getItem('token');
  // Decode the stored token (symbol-substitution → valid JWT) before sending
  const token = rawToken ? assignLetters(rawToken) : null;

  // Guard against a trailing slash on the base URL meeting a leading slash on
  // the path — a double slash makes AWS API Gateway treat it as a different
  // resource and return 403.
  const requestUrl = `${String(baseUrl).replace(/\/+$/, '')}/${String(url).replace(/^\/+/, '')}`;
  try {
    const mobiUrl: any = process.env.REACT_APP_API_apibaseurlemis4;
    if (requestUrl.includes(mobiUrl)) {
      authKey = process.env.REACT_APP_API_loginAuthorization;
    }

    const result = await axios({
      url: requestUrl,
      method,
      data: body,
      params,
      headers: {
        Accept: 'application/json, text/plain, */*',
        ...(service !== 'emisExt' &&
          service !== 'emisSample' &&
          authKey && { Authorization: authKey }),
        ...(service !== 'emisExt' &&
          service !== 'emisSample' &&
          token && { Token: token }),
        ...((service === 'emisExt' || service === 'emisSample') && {
          'x-api-key': process.env.REACT_APP_API_authorization1 || '',
        }),
        ...(argHeaders as Record<string, string>),
        ...(url == 'testnewlogin' && { 'X-Auth-Type': '1' }),
      },
    });

    return { data: result.data };
  } catch (error: any) {
    const status = error.response?.status ?? 'FETCH_ERROR';
    const responseData = error.response?.data ?? error.message;

    const errorMessage = getApiErrorMessage(status, responseData);
    toast.error(errorMessage);

    console.error('API Error:', {
      status,
      url: error.config?.url,
      method: error.config?.method,
      data: responseData,
      message: errorMessage,
    });

    if (status === 401) {
      const preservedBackUser = localStorage.getItem('backUser');
      localStorage.clear();
      // Restore backUser so "Back To User" still works after re-login
      if (preservedBackUser) {
        localStorage.setItem('backUser', preservedBackUser);
      }
      /**
       * Optional:
       * window.location.href = '/login';
       */
      window.location.href = '/';
    }

    return {
      error: {
        status,
        data: {
          originalError: responseData,
          message: errorMessage,
        },
      } as FetchBaseQueryError,
    };
  } finally {
    globalLoader.hide();
  }
};
